/**
 * 结算弹窗
 *
 * 设计（GDD 11 章验收 + 教育红线）：
 * - 星级逐颗点亮，但**失败不羞辱**：没通关时文案聚焦"下一次怎么做"；
 * - 分数拆解让孩子看懂分是怎么来的（点亮数 / 平均启蒙度 / 剩余回合 / 迷雾）；
 * - 新收集的知识卡横滑展示，点击直接翻阅图鉴 —— 把"学到了什么"放在最显眼的位置；
 * - 明确指出成绩由服务端复算（`servedBy`），离线时诚实说明"这次先在本地记账"。
 */

import type { KnowledgeCard } from "@chuandeng/shared";
import { esc, starsText } from "./dom.js";
import { type ModalHandle, openModal } from "./modal.js";

export interface SettlementView {
  levelTitle: string;
  levelIndex: number;
  outcome: "win" | "lose";
  stars: number;
  score: number;
  turns: number;
  turnLimit: number;
  mist: number;
  averageEnlightenment: number;
  goalEnlightenment: number;
  litCount: number;
  regionCount: number;
  newCards: readonly KnowledgeCard[];
  wrongCards: readonly KnowledgeCard[];
  /** 成绩来自服务端复算还是本地预演算 */
  servedBy: "server" | "local";
  lampOil?: number;
}

export interface SettlementOptions {
  onReplay(): void;
  onBack(): void;
  onOpenCodex(cardId: string): void;
  onReviewWrong?(cardId: string): void;
}

export function openSettlement(view: SettlementView, options: SettlementOptions): ModalHandle {
  const win = view.outcome === "win";
  const headLine = win
    ? "灯火相传，迷雾退散"
    : view.stars > 0
      ? "这一局稳住了，还能更好"
      : "迷雾暂时压过了火光";

  const nextHint = win
    ? view.stars >= 3
      ? "全图通明，这是满分的一局。"
      : "想拿三星，试试把每一个区域都点亮。"
    : `再试一次：优先给迷雾抗性的知识节点升级，或者把传播路线铺得更分散一些（目标 ${view.goalEnlightenment}）。`;

  const cardStream = (cards: readonly KnowledgeCard[], kind: "new" | "wrong"): string => {
    if (cards.length === 0) return "";
    return `
      <h3 class="panel-title" style="margin-top:1rem">
        ${kind === "new" ? "本局新收进图鉴" : "本局进了错题集"}
        <span>${cards.length} 张</span>
      </h3>
      <div class="card-stream">
        ${cards
          .map(
            (card) => `
          <button class="card-mini" type="button" data-card="${esc(card.id)}">
            <strong>${esc(card.title)}</strong>
            <span style="color:var(--ink-soft)">${esc(card.summary)}</span>
          </button>`,
          )
          .join("")}
      </div>`;
  };

  const bodyHtml = `
    <div class="settlement-stars" aria-label="获得 ${view.stars} 颗星">${starsText(view.stars)}</div>
    <p style="margin:.6rem 0 .2rem;font-size:1.05rem;font-weight:700">${esc(headLine)}</p>
    <p style="margin:0;color:var(--ink-soft);font-size:.88rem">${esc(nextHint)}</p>

    <div class="score-grid">
      <div class="score-cell"><span>得分</span><strong>${view.score}</strong></div>
      <div class="score-cell"><span>点亮区域</span><strong>${view.litCount} / ${view.regionCount}</strong></div>
      <div class="score-cell"><span>平均启蒙度</span><strong>${view.averageEnlightenment.toFixed(1)}</strong></div>
      <div class="score-cell"><span>用掉回合</span><strong>${view.turns} / ${view.turnLimit}</strong></div>
      <div class="score-cell"><span>结束时迷雾</span><strong>${Math.round(view.mist)}</strong></div>
      ${
        view.lampOil === undefined
          ? ""
          : `<div class="score-cell"><span>灯油</span><strong>${view.lampOil}</strong></div>`
      }
    </div>

    ${cardStream(view.newCards, "new")}
    ${cardStream(view.wrongCards, "wrong")}

    <p style="margin-top:1rem;font-size:.76rem;color:var(--ink-soft)">
      ${
        view.servedBy === "server"
          ? "成绩由服务端用同一份模拟核心复算得出（客户端上报的操作序列可被完整复核）。"
          : "当前处于离线状态：这一局先在本地记账，联网后会上报到云端并重新复核。"
      }
    </p>`;

  const actions = [
    { label: "复习错题", value: "review", kind: "ghost" as const, disabled: view.wrongCards.length === 0 },
    { label: "再玩一次", value: "replay", kind: "ghost" as const },
    { label: "回到关卡", value: "back" },
  ];

  const handle = openModal({
    eyebrow: `第 ${view.levelIndex} 关 · ${view.levelTitle}`,
    title: win ? "通关" : "本局结束",
    bodyHtml,
    actions,
    onAction: (value) => {
      if (value === "replay") {
        handle.close();
        options.onReplay();
      } else if (value === "back") {
        handle.close();
        options.onBack();
      } else if (value === "review") {
        const first = view.wrongCards[0];
        if (first) options.onReviewWrong?.(first.id);
      }
    },
  });

  handle.element.querySelector(".modal-body")?.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLElement>("[data-card]");
    const cardId = button?.dataset["card"];
    if (cardId) options.onOpenCodex(cardId);
  });

  return handle;
}
